import { apiFetch } from '@/lib/api';
import type { ComputedShare } from '@domain/split';
import type { ExpenseParticipant, ExpenseWithDetails, SplitType } from '@domain/types';

export type ExpenseInput = {
  groupId: string;
  title: string;
  categoryId: string | null;
  amountMinor: number;
  paidBy: string;
  date: string;
  notes?: string | null;
  splitType: SplitType;
  shares: ComputedShare[];
};

export type ExpenseFilters = {
  search?: string;
  categoryIds?: string[];
  memberIds?: string[];
  splitTypes?: SplitType[];
  dateFrom?: string;
  dateTo?: string;
  uncategorisedOnly?: boolean;
};

function buildQueryString(filters: ExpenseFilters): string {
  const params = new URLSearchParams();
  if (filters.search?.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters.uncategorisedOnly) {
    params.append('uncategorisedOnly', 'true');
  } else if (filters.categoryIds?.length) {
    filters.categoryIds.forEach((id) => params.append('categoryIds', id));
  }
  if (filters.memberIds?.length) {
    filters.memberIds.forEach((id) => params.append('memberIds', id));
  }
  if (filters.splitTypes?.length) {
    filters.splitTypes.forEach((type) => params.append('splitTypes', type));
  }
  if (filters.dateFrom) {
    params.append('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.append('dateTo', filters.dateTo);
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listExpenses(
  groupId: string,
  filters: ExpenseFilters = {}
): Promise<ExpenseWithDetails[]> {
  const qs = buildQueryString(filters);
  return apiFetch<ExpenseWithDetails[]>(`/groups/${groupId}/expenses${qs}`);
}

export async function getExpense(id: string): Promise<ExpenseWithDetails | null> {
  try {
    return await apiFetch<ExpenseWithDetails>(`/expenses/${id}`);
  } catch (error) {
    if ((error as Error).message.includes('404')) return null;
    throw error;
  }
}

export async function createExpense(input: ExpenseInput): Promise<string> {
  return apiFetch<string>('/expenses', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateExpense(id: string, input: ExpenseInput): Promise<void> {
  await apiFetch<void>(`/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteExpense(id: string): Promise<void> {
  await apiFetch<void>(`/expenses/${id}`, {
    method: 'DELETE',
  });
}

export async function listParticipantsForGroup(
  groupId: string
): Promise<ExpenseParticipant[]> {
  const expenses = await listExpenses(groupId);
  return expenses.flatMap((e) => e.participants);
}
